/**
 * QuotaUsageTable 组件
 *
 * 以表格形式展示配额使用详情。
 */

import React from 'react';
import type { SearchQuotaItem } from '../../../services/searchAdminApi';

interface QuotaUsageTableProps {
  quotas: SearchQuotaItem[];
  loading: boolean;
}

const quotaTypeLabels: Record<string, string> = {
  concurrency: '并发',
  requests_per_minute: 'RPM',
  requests_per_hour: '每小时',
  requests_per_day: '每日',
  requests_per_month: '每月',
  requests_total: '总计',
};

export const QuotaUsageTable: React.FC<QuotaUsageTableProps> = ({
  quotas,
  loading,
}) => {
  if (loading) {
    return (
      <div className="text-center py-8 text-gray-500">加载中...</div>
    );
  }

  if (quotas.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">暂无配额使用数据</div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50">
            <th className="text-left p-3 font-medium text-gray-600">供应商</th>
            <th className="text-left p-3 font-medium text-gray-600">密钥</th>
            <th className="text-left p-3 font-medium text-gray-600">
              配额类型
            </th>
            <th className="text-right p-3 font-medium text-gray-600">已用</th>
            <th className="text-right p-3 font-medium text-gray-600">限额</th>
            <th className="text-right p-3 font-medium text-gray-600">剩余</th>
            <th className="text-right p-3 font-medium text-gray-600">
              使用率
            </th>
          </tr>
        </thead>
        <tbody>
          {quotas.map((q, idx) => (
            <tr
              key={`${q.provider_name}-${q.key_index}-${q.quota_type}`}
              className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
            >
              <td className="p-3 font-medium">{q.provider_name}</td>
              <td className="p-3">#{q.key_index}</td>
              <td className="p-3">
                {quotaTypeLabels[q.quota_type] || q.quota_type}
              </td>
              <td className="text-right p-3">{q.used}</td>
              <td className="text-right p-3">{q.limit}</td>
              <td className="text-right p-3">{q.remaining}</td>
              <td className="text-right p-3">
                <div className="flex items-center justify-end gap-2">
                  <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        q.usage_percent >= 95
                          ? 'bg-red-500'
                          : q.usage_percent >= 80
                            ? 'bg-yellow-500'
                            : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(q.usage_percent, 100)}%` }}
                    />
                  </div>
                  <span
                    className={`text-xs ${
                      q.usage_percent >= 95
                        ? 'text-red-600'
                        : q.usage_percent >= 80
                          ? 'text-yellow-600'
                          : 'text-green-600'
                    }`}
                  >
                    {q.usage_percent.toFixed(1)}%
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default QuotaUsageTable;
